import { AttachmentSourceType, Event } from "@markit/common.types";
import { Colors } from "../../../../utils/colors";
import ContentViolationTagModal from "../../../DeliveryScore/ContentViolationTagModal";
import { LightTheme, useTheme } from "../../../../hooks/useTheme";
import { AttachmentsActionBarHoldItem } from "./AttachmentsActionBarHoldItem";
import { AttachmentsMediaButton } from "./AttachmentsMediaButton";
import { Icon } from "@iconify/react";
import TextCreditsTagModal from "./TextCreditsTagModal";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { useMemo } from "react";
import {
  calculateCharactersPerTextCredit,
  deliveryScoreGuidelineOne,
  calculateMessagePluginsNumCharacters,
} from "@markit/common.utils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";

type AttachmentsActionBarProps = {
  message: string;
  numCredits: number;
  image?: string;
  setImage?: (image: string) => void;
  actionItemOnPress?: (key: string) => void;
  attachmentSourceType?: AttachmentSourceType;
  disableEventLinks?: boolean;
  hideActionItems?: boolean;
  event?: Event;
  showComplianceCreditNotice?: boolean;
};

export const AttachmentsActionBar = (props: AttachmentsActionBarProps) => {
  const {
    message,
    numCredits,
    image,
    setImage,
    actionItemOnPress,
    attachmentSourceType,
    disableEventLinks,
    hideActionItems,
    event,
    showComplianceCreditNotice,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { theme } = useTheme();

  const messageNumCharacters = useMemo(
    () =>
      calculateMessagePluginsNumCharacters(
        message,
        accountData.trustedSender,
        event
      ),
    [accountData.trustedSender, event, message]
  );

  const numCharactersPerCredit = useMemo(
    () => calculateCharactersPerTextCredit(message, messageNumCharacters),
    [message, messageNumCharacters]
  );

  return (
    <StandardBorderedContainer
      containerStyles={{
        backgroundColor: Colors.WHITE1,
        padding: 14,
        borderRadius: 12,
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <div
        className="AlignedRow onboarding-send-first-text-credits-target"
        style={{ gap: 14 }}
      >
        <div style={{ width: 40, height: 40, position: "relative" }}>
          <CircularProgressbar
            value={
              messageNumCharacters > 1500
                ? 100
                : ((((messageNumCharacters - 1) % numCharactersPerCredit) + 1) /
                    numCharactersPerCredit) *
                  100
            }
            strokeWidth={10}
            styles={buildStyles({
              strokeLinecap: "round",
              pathColor: deliveryScoreGuidelineOne(message, numCredits).color,
              trailColor: Colors.GRAY2,
            })}
          />
          <div
            style={{
              position: "absolute",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <span
              className="sectionTitle"
              style={{ fontSize: messageNumCharacters > 999 ? 11 : 12 }}
            >
              {messageNumCharacters > 1500
                ? 1500 - messageNumCharacters
                : messageNumCharacters}
            </span>
          </div>
        </div>
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <ContentViolationTagModal
            message={message}
            numCredits={numCredits}
            theme={theme ?? LightTheme}
          />
          <TextCreditsTagModal
            message={message}
            mediaUrl={image}
            event={event}
            showCreditsNotice
            showComplianceCreditNotice={showComplianceCreditNotice}
          />
        </div>
      </div>
      {!hideActionItems ? (
        <div
          className="AlignedRow onboarding-send-first-text-attach-target"
          style={{ gap: 7 }}
        >
          {setImage ? (
            <AttachmentsMediaButton
              image={image ?? ""}
              setImage={setImage}
              theme={theme}
            >
              <div
                className="MassTextsAttachmentButton"
                style={{
                  alignSelf: "flex-end",
                  backgroundColor: theme.ButtonSecondaryBG.backgroundColor,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <Icon
                  icon="ion:image"
                  height={20}
                  style={{
                    color: theme?.ButtonText.color,
                  }}
                />
              </div>
            </AttachmentsMediaButton>
          ) : null}
          {actionItemOnPress && attachmentSourceType ? (
            <AttachmentsActionBarHoldItem
              onClick={actionItemOnPress}
              attachmentSourceType={attachmentSourceType}
              theme={theme}
              invertedColor={true}
              event={event}
              disabledEventLinks={disableEventLinks}
            />
          ) : null}
        </div>
      ) : null}
    </StandardBorderedContainer>
  );
};
